import { useEffect, useRef, useState } from 'react';
import {
  getTransaction, getRecentTransactions, resendCallback, searchTransactions, updateAwaitingDepositStatus,
  rejectUnpaidDeposit,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import PendingStatusModal from '../components/PendingStatusModal';
import { awaitingModalRecord } from '../components/BulkPendingStatusModal';

// Field the search box filters on - merchant_order_id is the only one with
// a direct O(1) Redis key read (transactionLookup()); the other three go
// through searchTransactions()'s scan-based search instead, which returns a
// LIST of matches rather than one record - see that function's docblock for
// why (none of these three are a primary key anywhere in Redis) and its
// known limitations (won't find something already succeeded and expired).
const SEARCH_FIELDS = [
  { value: 'merchant_order_id', label: 'Merchant order ID' },
  { value: 'merchant_id', label: 'Merchant ID' },
  { value: 'instruction_id', label: 'Instruction ID' },
  { value: 'qrph_invoice', label: 'QRPh invoice' },
];

// Ordered so the table below reads top-to-bottom as a timeline, not just an
// alphabetical field dump - each row is one stage this repo's own pipeline
// can attest to. See P2MDashboard::transactionLookup()'s docblock for why
// this stops at "sent by callback worker" rather than the full received-by-
// Router -> sent-to-Bancnet -> Bancnet-callback chain that was originally
// asked for: those earlier stages live in a separate repo (instapay-router)
// whose logs aren't queryable from here yet.
// Each row's `id` is only for React's key prop (two rows below intentionally
// read the same underlying `resultKey` - see "Merchant acknowledged" below -
// so `id` and `resultKey` are kept separate rather than reusing one field
// for both).
const TIMELINE_FIELDS = [
  {
    id: 'created_at',
    resultKey: 'created_at',
    label: 'Received / written to Redis',
    hint: 'When this service’s own callback record was first created.',
  },
  {
    id: 'sent_at',
    resultKey: 'sent_at',
    label: 'Fetched by poller',
    hint: 'When PollPendingCallbacks handed this off to SQS.',
    // Added 2026-07-28 (same pass as the "stuck at status=sent"
    // auto-recovery fix) - a record processed before that field existed, or
    // before the poller service has actually been redeployed with it, will
    // have no sent_at at all even though it obviously WAS fetched by the
    // poller (a worker can't attempt a job the poller never dispatched).
    // "not reached" would be actively wrong there, so this stage alone
    // falls back to inferring "it happened, we just don't have the
    // timestamp" whenever there's other evidence the worker ran at all.
    inferOccurredIfMissing: (r) => Boolean(r.attempts > 0 || r.last_attempted_at || r.response !== null),
  },
  {
    id: 'last_attempted_at',
    resultKey: 'last_attempted_at',
    label: 'Sent by callback worker',
    hint: 'When SendNotification last attempted delivery to the merchant (this timestamp is recorded after the response comes back - see "Merchant acknowledged" below for how long that took).',
  },
  {
    // Deliberately reads the same field as the row above - added 2026-07-28
    // per explicit request for a "when did the merchant receive/acknowledge
    // it" timestamp, then corrected the same day: last_attempted_at is
    // written AFTER the HTTP response comes back, so both rows show the
    // same timestamp, but that does NOT mean send and acknowledge were
    // simultaneous - under high TPS the merchant's own queueing/processing
    // delay can make the round-trip take real time (up to the full
    // connectTimeout(5)+timeout(10) ceiling). last_attempt_duration_ms
    // (added the same fix) captures that actual round-trip length, shown
    // as an annotation in describeStage() below rather than claiming
    // "same moment" outright.
    id: 'merchant_acknowledged',
    resultKey: 'last_attempted_at',
    label: 'Merchant acknowledged',
    hint: 'When the merchant’s callback_url returned its HTTP response.',
  },
  {
    id: 'next_retry_at',
    resultKey: 'next_retry_at',
    label: 'Next retry scheduled',
    hint: 'Only set while status is "failed" and attempts remain.',
  },
  {
    id: 'exhausted_at',
    resultKey: 'exhausted_at',
    label: 'Exhausted (gave up)',
    hint: 'Only set once max_attempts is reached with no success.',
  },
];

function describeStage(field, result) {
  const value = result[field.resultKey];
  if (value) {
    // sent_at can now be backfilled by the BFF from a CloudWatch Logs
    // search when Redis has the record but not this specific field (see
    // index.js's poller-only fallback, 2026-07-28) - annotated rather than
    // shown as an indistinguishable plain timestamp, since it's read from
    // log text rather than the structured Redis record everything else
    // here comes from.
    let annotation = '';
    if (field.resultKey === 'sent_at' && result.sent_at_source === 'cloudwatch_logs') {
      annotation = ' (from CloudWatch logs)';
    } else if (field.id === 'merchant_acknowledged' && typeof result.last_attempt_duration_ms === 'number') {
      // The actual merchant round-trip length, not just "when" - this is
      // what makes it clear the send and the acknowledgment weren't
      // necessarily the same instant, especially under high TPS.
      annotation = ` (took ${result.last_attempt_duration_ms.toLocaleString()}ms to respond)`;
    }
    return { text: `${value}${annotation}`, muted: false };
  }
  if (field.inferOccurredIfMissing && field.inferOccurredIfMissing(result)) {
    return { text: 'occurred — timestamp not recorded (predates sent_at tracking)', muted: true };
  }
  return { text: 'not reached', muted: true };
}

const STATUS_BADGES = {
  success: 'text-bg-success',
  sent: 'text-bg-info',
  pending: 'text-bg-secondary',
  failed: 'text-bg-warning',
  exhausted: 'text-bg-danger',
  awaiting_bancnet: 'text-bg-warning',
  awaiting_leg3: 'text-bg-warning',
  awaiting_payment: 'text-bg-light',
};

const STATUS_LABELS = {
  awaiting_bancnet: 'awaiting Bancnet',
  awaiting_leg3: 'awaiting Bancnet',
  awaiting_payment: 'awaiting payment',
};

// Audit flow (2026-10-10): the transaction's path through instapay-router
// and this service, one step per hand-off, with the first step that has not
// happened marked as where it stopped. Deposits and withdrawals take
// different paths through Bancnet, so each has its own steps. A step counts
// as done when its own evidence exists OR any later step's does (older
// records predate some timestamps; a record can't reach the worker without
// passing the poller).
const isWithdrawal = (r) => (r.transaction_type || r.router_txn?.transaction_type) === 'ICRT';
// Static QR deposits (QR ID 88): Java pushes no order, so the flow starts at Leg 3.
const isStaticDeposit = (r) =>
  !isWithdrawal(r)
  && (r.deposit_type || r.router_txn?.deposit_type || r.router_txn?.record?.ips_instr?.deposit_type) === 'static';
const hasFinalCallback = (r) => r.source !== 'router_txn' && r.notification_status !== 'submitted';

const POLLER_STEP = {
  id: 'poller',
  label: 'Queued by poller',
  detail: 'PollPendingCallbacks handed the callback to SQS.',
  time: (r) => r.sent_at,
  evidence: (r) => hasFinalCallback(r) && Boolean(r.sent_at || r.attempts > 0 || r.last_attempted_at || r.response !== null),
  stuck: () => 'The callback is waiting in the pending index for the poller to pick it up.',
};
const DELIVERED_STEP = {
  id: 'delivered',
  label: 'Callback delivered to Java',
  detail: 'Java’s callback URL accepted the notification.',
  time: (r) => (r.status === 'success' ? r.last_attempted_at : null),
  evidence: (r) => hasFinalCallback(r) && r.status === 'success',
  stuck: (r) =>
    r.status === 'exhausted'
      ? 'Every delivery attempt failed - retry it from the Retry-exhausted page.'
      : r.status === 'failed'
        ? `The last delivery attempt failed${r.next_retry_at ? `; next retry at ${r.next_retry_at}` : ''}.`
        : 'Waiting for a worker to send it.',
};

// When the router answered Leg 3: p2m:txn.validated_at, falling back to the
// ips:instr record's accepted_at.
function leg3ValidatedAt(r) {
  const rec = r.router_txn?.record || {};
  return rec.p2m_txn?.validated_at || rec.ips_instr?.accepted_at || null;
}

// Leg 3 happened: the Leg 3 scan saw it, the router stamped validated_at /
// instr_id on p2m:txn, or Java already got the post-ACTC "submitted" notice.
function hasLeg3Evidence(r) {
  const txn = r.router_txn?.record?.p2m_txn || {};
  // A deposit callback carrying an instruction ID also means Leg 3 happened
  // (Bancnet assigns it there); the no-Leg-3 rejection sends none.
  return Boolean(r.leg3 || leg3ValidatedAt(r) || txn.instr_id || r.router_txn?.record?.ips_instr
    || r.instruction_id || r.notification_status === 'submitted');
}

// ACTC / "RJCT code" for the deposit's Leg 3, or null if unknown. A manual
// send also sets p2m:txn's status, so a "rejected" there is only the router's
// Leg 3 rejection when the router recorded it before the manual send (or the
// send recorded prior_router_status itself).
function leg3Result(r) {
  if (r.leg3) {
    return r.leg3.result === 'RJCT' ? `RJCT ${r.leg3.rejection_code || ''}`.trim() : 'ACTC';
  }
  if (!hasLeg3Evidence(r)) {
    return null;
  }
  const txn = r.router_txn?.record?.p2m_txn || {};
  const m = manualCallback(r);
  const rjct = (code) => `RJCT ${code || ''}`.trim();
  if (m && 'prior_router_status' in m) {
    return m.prior_router_status === 'rejected' ? rjct(m.prior_rejection_code) : 'ACTC';
  }
  if (txn.status === 'rejected') {
    const routerRejected = !m || (txn.rejected_at && m.updated_at && txn.rejected_at < m.updated_at);
    return routerRejected ? rjct(txn.rejection_code) : 'ACTC';
  }
  return 'ACTC';
}

// A status ops sent by hand: the callback record's own manual_status_update
// (Awaiting / no-Leg-3 sends) or the dashboard's sent marker (any send,
// including Java Pending via the router).
export function manualCallback(r) {
  const m = r.manual_status_update || r.manual_update;
  return m && m.status ? m : null;
}

function manualStep(m) {
  const code = m.error_message ? ` (${m.error_message})` : '';
  const by = m.updated_by ? ` by ${m.updated_by}` : '';
  return {
    id: 'manual',
    label: 'Manual callback sent',
    detail: `Status “${m.status}”${code} was sent to Java from the dashboard${by}, after checking Bancnet’s records.`,
    time: () => m.updated_at || null,
    evidence: () => true,
    stuck: () => null,
  };
}

// The step a manual send stands in for: Bancnet's final result never came.
const MANUAL_REPLACES = { deposit: 'leg5', withdrawal: 'leg3' };

const DEPOSIT_STEPS = [
  {
    id: 'order',
    label: 'Order created',
    detail: 'Java created the order and QR and pushed it to the router (p2m:txn).',
    time: (r) => r.router_txn?.created_at || null,
    evidence: (r) => Boolean(r.router_txn),
    stuck: () => 'The router has no record of this order.',
  },
  {
    id: 'leg3',
    label: 'Payment validated (Leg 3)',
    detail: 'Bancnet asked the router to validate the order; the router answered ACTC (valid) or RJCT.',
    time: (r) => {
      return [leg3Result(r), leg3ValidatedAt(r)].filter(Boolean).join(' · ') || null;
    },
    evidence: hasLeg3Evidence,
    stuck: (r) =>
      r.unpaid?.can_reject
        ? 'No Leg 3 reached the router. Either the customer never paid, or Leg 3 was lost (Bancnet tags it DS24). Check Bancnet, then send the rejection to Java below.'
        : 'No payment has reached the router yet: the customer may still be paying, or Leg 3 was lost on the way.',
  },
  {
    id: 'leg5',
    label: 'Bancnet final result (Leg 5)',
    detail: 'Bancnet sent the final result; the router queued a callback for Java.',
    time: (r) => (hasFinalCallback(r) ? r.created_at : null),
    evidence: hasFinalCallback,
    stuck: (r) =>
      r.notification_status === 'submitted'
        ? `Bancnet’s final result never reached the router, so Java only has the “payment accepted, pending confirmation” notice${r.last_attempted_at ? ` (sent ${r.last_attempted_at})` : ''}. This deposit is on the Java Pending card; once confirmed in Bancnet, update it there.`
        : 'Bancnet’s final result never reached the router (network congestion or flapping), so Java was not told. Check the transaction in Bancnet’s records, then send the status to Java below.',
  },
  POLLER_STEP,
  DELIVERED_STEP,
];

// Static QR: Leg 3 is the first step. Its evidence is the ips:instr record
// (Lookup finds a stuck one through the Awaiting card's scan) or any later step.
const STATIC_STEPS = DEPOSIT_STEPS.filter((step) => step.id !== 'order').map((step) =>
  step.id === 'leg3'
    ? {
      ...step,
      detail: 'The customer paid the merchant’s static QR; Bancnet asked the router to validate it. Java pushes no order for a static QR, so the flow starts here.',
      stuck: () => 'The router has no Leg 3 record for this static QR payment.',
    }
    : step
);

const WITHDRAWAL_STEPS = [
  {
    id: 'sent',
    label: 'Withdrawal sent',
    detail: 'Java asked the router to send it; the router forwarded it to Bancnet (p2m:txn).',
    time: (r) => r.router_txn?.created_at || null,
    evidence: (r) => Boolean(r.router_txn),
    stuck: () => 'The router has no record of this withdrawal.',
  },
  {
    id: 'leg3',
    label: 'Bancnet result (Leg 3)',
    detail: 'Bancnet returned paid or rejected; the router queued a callback for Java.',
    time: (r) => (hasFinalCallback(r) ? r.created_at : null),
    evidence: hasFinalCallback,
    stuck: (r) =>
      r.notification_status === 'submitted'
        ? 'Bancnet has not answered. The router sent Java a “no response yet” notice; this withdrawal is on the Java Pending card.'
        : 'No result from Bancnet yet. After 5 minutes the router sends Java a “no response yet” notice.',
  },
  POLLER_STEP,
  DELIVERED_STEP,
];

export function buildAuditFlow(result) {
  const withdrawal = isWithdrawal(result);
  const manual = manualCallback(result);
  const replaces = MANUAL_REPLACES[withdrawal ? 'withdrawal' : 'deposit'];
  // Deposit rejected from the no-Leg-3 card: neither Leg 3 nor Leg 5 came.
  const noLeg3 = manual && !withdrawal && (manual.source === 'no_leg3' || !hasLeg3Evidence(result));
  const skipped = manual ? (noLeg3 ? ['leg3', replaces] : [replaces]) : [];
  const steps = [...(withdrawal ? WITHDRAWAL_STEPS : isStaticDeposit(result) ? STATIC_STEPS : DEPOSIT_STEPS)];
  if (manual) {
    steps.splice(steps.findIndex((s) => s.id === replaces) + 1, 0, manualStep(manual));
  }
  const reached = steps.map((step) => step.evidence(result));
  const done = reached.map((_, i) => reached.slice(i).some(Boolean));
  const stoppedAt = done.indexOf(false);
  return steps.map((step, i) => {
    let state = done[i] ? 'done' : i === stoppedAt ? 'stopped' : 'not_reached';
    let stuckText = i === stoppedAt ? step.stuck(result) : null;
    let time = step.time(result) || null;
    if (skipped.includes(step.id)) {
      state = 'skipped';
      stuckText = step.id === 'leg3' && !withdrawal
        ? 'No payment validation (Leg 3) reached the router; the rejection was sent manually.'
        : 'Bancnet’s final result did not reach the router; the status was sent manually instead.';
      time = null;
    }
    return { id: step.id, label: step.label, detail: step.detail, time, state, stuckText };
  });
}

function AuditFlow({ result }) {
  const steps = buildAuditFlow(result);
  return (
    <ol className="list-unstyled mb-0">
      {steps.map((step, i) => {
        const dot =
          step.state === 'done' ? (step.id === 'manual' ? 'bg-info' : 'bg-success')
            : step.state === 'stopped' ? 'bg-warning'
              : step.state === 'skipped' ? 'bg-secondary' : 'bg-secondary-subtle';
        return (
          <li key={step.id} className="d-flex gap-3" data-step={step.id} data-state={step.state}>
            <div className="d-flex flex-column align-items-center" style={{ width: '1.25rem' }}>
              <span
                className={`rounded-circle ${dot} d-inline-block flex-shrink-0`}
                style={{ width: '0.85rem', height: '0.85rem', marginTop: '0.3rem' }}
                aria-hidden="true"
              />
              {i < steps.length - 1 && <span className="flex-grow-1 border-start my-1" style={{ minHeight: '1rem' }} />}
            </div>
            <div className="pb-3 flex-grow-1">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className={step.state === 'not_reached' ? 'text-muted' : 'fw-semibold'}>{step.label}</span>
                {step.state === 'done' && step.id === 'manual' && <span className="badge text-bg-info">manual</span>}
                {step.state === 'done' && step.id !== 'manual' && <span className="badge text-bg-success">done</span>}
                {step.state === 'skipped' && <span className="badge text-bg-secondary">not received</span>}
                {step.state === 'stopped' && <span className="badge text-bg-warning">stopped here</span>}
                {step.state === 'not_reached' && <span className="badge text-bg-light text-muted">not reached</span>}
                <span className="text-muted small ms-auto">
                  {step.state === 'not_reached' ? '' : step.time || (step.state === 'done' ? 'time not recorded' : '')}
                </span>
              </div>
              <div className="text-muted small">{step.detail}</div>
              {step.stuckText && (
                <div className={`small mt-1 ${step.state === 'skipped' ? 'text-muted' : 'text-warning-emphasis'}`}>{step.stuckText}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ManualUpdateNote({ marker, waiting }) {
  if (!marker) {
    return null;
  }
  return (
    <div className="alert alert-info py-2 mt-3 mb-0 small">
      Status <strong>{marker.status}</strong> was sent to Java
      {marker.updated_by ? <> by {marker.updated_by}</> : null}
      {marker.updated_at ? <> at {marker.updated_at}</> : null}
      {marker.error_message ? <> ({marker.error_message})</> : null}.
      {waiting && <>{' '}It stays listed here until the callback record is created.</>}
    </div>
  );
}

// One card per outcome (2026-07-28, replacing an earlier single mixed-status
// "last 10" card) - each card is already scoped to one outcome by which list
// it's given, so no per-row status badge is needed the way the single-list
// version needed one. "When" picks whichever timestamp is most meaningful
// for that outcome (exhausted_at for the exhausted card, otherwise the last
// attempt time, falling back to when the record was first created).
function RecentOutcomeCard({ title, hint, items, loading, onRowClick }) {
  return (
    <div className="col-lg-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <h2 className="h6 mb-1">{title}</h2>
          <p className="text-muted small mb-3">{hint}</p>

          {loading && items.length === 0 && <div className="text-muted small">Loading…</div>}

          {!loading && items.length === 0 && <div className="text-muted small">None yet.</div>}

          {items.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>merchant_order_id</th>
                    <th>Merchant</th>
                    <th>Type</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((txn) => (
                    <tr
                      key={txn.merchant_order_id}
                      role="button"
                      onClick={() => onRowClick(txn.merchant_order_id)}
                    >
                      <td className="text-truncate" style={{ maxWidth: '9rem' }}>
                        {txn.merchant_order_id}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: '6rem' }}>
                        {txn.merchant_id ?? '—'}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: '6rem' }}>
                        {txn.transaction_type ?? '—'}
                      </td>
                      <td className="text-nowrap">
                        {txn.exhausted_at || txn.last_attempted_at || txn.sent_at || txn.created_at || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Manual resend of an ALREADY-SUCCESSFUL callback (2026-10-08) - see
// P2MJava::resendCallback(). Only rendered for success records (and for
// aged-out records, where the server checks the stored status itself).
// Failed/exhausted callbacks are not offered this: they retry on their own,
// or from the Retry-exhausted page.
//
// Two clicks on purpose: the merchant really does receive the notification
// a second time, so the first click only explains that and the second one
// sends.
function ResendControl({ merchantOrderId, callbackUrl, agedOut, onQueued }) {
  const { email } = useAuth();
  const [stage, setStage] = useState('idle'); // idle | confirm | sending
  const [error, setError] = useState(null);

  async function handleConfirm() {
    setStage('sending');
    setError(null);
    const res = await resendCallback(merchantOrderId, email);
    if (res.ok) {
      setStage('idle');
      onQueued(res);
    } else {
      setStage('confirm');
      setError(res.message || 'Resend failed.');
    }
  }

  if (stage === 'idle') {
    return (
      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => { setError(null); setStage('confirm'); }}
        >
          Resend callback
        </button>
      </div>
    );
  }

  return (
    <div className="alert alert-warning mt-3 mb-0 small">
      <div className="fw-semibold mb-1">Resend this callback?</div>
      <div>
        The merchant
        {callbackUrl ? <> at <code style={{ wordBreak: 'break-all' }}>{callbackUrl}</code></> : null}
        {' '}will receive this notification again, even though the first delivery succeeded. It
        goes through the normal queue with the usual retries, and it is not counted again in
        today&apos;s totals.
      </div>
      {agedOut && (
        <div className="mt-1">
          The live record has expired, so it will be rebuilt from the stored transaction history.
          The server only resends it if that history says the callback succeeded.
        </div>
      )}
      {error && <div className="text-danger mt-2">{error}</div>}
      <div className="d-flex gap-2 justify-content-end mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => { setStage('idle'); setError(null); }}
          disabled={stage === 'sending'}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleConfirm}
          disabled={stage === 'sending'}
        >
          {stage === 'sending' ? 'Queuing…' : 'Confirm resend'}
        </button>
      </div>
    </div>
  );
}

export default function TransactionLookup() {
  const [query, setQuery] = useState('');
  const [searchField, setSearchField] = useState('merchant_order_id');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Only populated by the merchant_id/instruction_id/qrph_invoice search
  // path (searchTransactions() returns a list, not one record) - null for
  // the merchant_order_id path, which goes straight to `result` instead.
  const [searchResults, setSearchResults] = useState(null);
  // Confirmation from the last resend. Tied to a merchant_order_id so it
  // only shows while that same transaction is on screen - every way of
  // navigating away changes `result`, so nothing has to remember to clear it.
  const [resendNotice, setResendNotice] = useState(null);
  // "Send status to Java" for a transaction stuck awaiting Bancnet.
  const [statusModalRecord, setStatusModalRecord] = useState(null);
  const [unpaidModalRecord, setUnpaidModalRecord] = useState(null);
  const resendRefreshTimer = useRef(null);

  // Last 10 transactions per outcome (success/failed/exhausted) this
  // pipeline has touched (2026-07-28) - fetched once on page load,
  // independent of the search box above/below it. Kept as its own
  // loading/error state since a failure here (e.g. the BFF or Lumen being
  // briefly unreachable) shouldn't block someone from still searching for a
  // specific ID. One request returns all three lists together (see
  // P2MDashboard::recentTransactions()), not three separate round trips.
  const [recent, setRecent] = useState({ success: [], failed: [], exhausted: [] });
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(null);

  async function loadRecent() {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const data = await getRecentTransactions(10);
      setRecent({
        success: data.success || [],
        failed: data.failed || [],
        exhausted: data.exhausted || [],
      });
    } catch (err) {
      setRecentError(err.message || 'Could not load recent transactions.');
    } finally {
      setRecentLoading(false);
    }
  }

  useEffect(() => {
    loadRecent();
    return () => clearTimeout(resendRefreshTimer.current);
  }, []);

  // After a resend is queued: refresh now (status flips back to pending),
  // and once more a few seconds later, by which point the poller and a
  // worker have normally sent it and the new attempt is in Attempt history.
  function handleResendQueued(merchantOrderId, res) {
    setResendNotice({ merchantOrderId, message: res.message, requestedAt: res.requested_at });
    runSearch(merchantOrderId);
    clearTimeout(resendRefreshTimer.current);
    resendRefreshTimer.current = setTimeout(() => runSearch(merchantOrderId), 6000);
  }

  // Deliberately does NOT clear searchResults - when this is called from a
  // row click on a field-search results list (handleRecentRowClick below),
  // keeping that list around means the "back" button can return to it
  // instead of always landing on "recent transactions". The one place that
  // DOES need to clear it - a fresh merchant_order_id form submission - does
  // so itself before calling this.
  async function runSearch(merchantOrderId) {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await getTransaction(merchantOrderId);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Lookup failed.');
    } finally {
      setLoading(false);
    }
  }

  // merchant_id/instruction_id/qrph_invoice path - returns a list of
  // matches (searchTransactions()'s scan-based search), not one record, so
  // this populates searchResults instead of result. See that function's
  // docblock for the scope/limitations of what it actually searches.
  async function runFieldSearch(field, value) {
    setLoading(true);
    setError(null);
    setResult(null);
    setSearchResults(null);
    try {
      const data = await searchTransactions(field, value);
      setSearchResults(data);
    } catch (err) {
      setError(err.message || 'Search failed.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const value = query.trim();
    if (!value) {
      return;
    }
    if (searchField === 'merchant_order_id') {
      setSearchResults(null);
      await runSearch(value);
    } else {
      await runFieldSearch(searchField, value);
    }
  }

  // Switching the search-by field clears whatever's currently shown -
  // otherwise a stale single-record detail view or results list could sit
  // on screen looking like it matches the newly-selected field when it
  // doesn't.
  function handleFieldChange(field) {
    setSearchField(field);
    setResult(null);
    setSearchResults(null);
    setError(null);
  }

  // Clicking a row in the "recent transactions" list, or a row in a
  // merchant_id/instruction_id/qrph_invoice search's results list, re-uses
  // the exact same lookup path as a manual merchant_order_id search
  // (including the CloudWatch-fallback/stale-sent_at-backfill behavior in
  // getTransaction()) rather than rendering that row's own already-fetched
  // fields directly - these rows are typically fetched only seconds before
  // being clicked, so a fresh lookup is cheap and guarantees the detail
  // view is never stale relative to what's clicked.
  function handleRecentRowClick(merchantOrderId) {
    setQuery(merchantOrderId);
    setSearchField('merchant_order_id');
    runSearch(merchantOrderId);
  }

  return (
    <div>
      <h1 className="h4 mb-2">Transaction audit lookup</h1>

      <form className="d-flex gap-2 mb-4" style={{ maxWidth: '640px' }} onSubmit={handleSubmit}>
        <select
          className="form-select"
          style={{ maxWidth: '180px' }}
          value={searchField}
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          {SEARCH_FIELDS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="form-control"
          placeholder={SEARCH_FIELDS.find((f) => f.value === searchField)?.label || searchField}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <button type="submit" className="btn btn-primary" disabled={loading || !query.trim()}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {searchField !== 'merchant_order_id' && (
        <p className="text-muted small" style={{ maxWidth: '90%', marginTop: '-0.75rem' }}>
          Searches what&rsquo;s currently pending or exhausted in Redis, plus already-succeeded
          transactions (added 2026-08-06) - but only for transactions that succeeded after that fix
          shipped, since older successes were never indexed retroactively. If a search for an
          already-succeeded transaction comes up empty, try Merchant order ID instead - that path
          reads the record directly and also falls back to CloudWatch Logs once it expires.
        </p>
      )}

      {searchResults && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="h6 mb-0">
                {searchResults.total} match{searchResults.total === 1 ? '' : 'es'} for {searchResults.field} =
                &ldquo;{searchResults.value}&rdquo;
              </h2>
              <button
                type="button"
                className="btn btn-sm btn-link px-0"
                onClick={() => {
                  setSearchResults(null);
                  setQuery('');
                }}
              >
                Clear
              </button>
            </div>

            {searchResults.truncated && (
              <div className="alert alert-warning py-2 small">
                More matches exist than shown here (capped at {searchResults.results.length}) -
                narrow the search if you don&rsquo;t see what you&rsquo;re looking for.
              </div>
            )}

            {searchResults.results.length === 0 ? (
              <div className="text-muted small">No matches.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>merchant_order_id</th>
                      <th>Merchant</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.results.map((txn) => (
                      <tr
                        key={txn.merchant_order_id}
                        role="button"
                        onClick={() => handleRecentRowClick(txn.merchant_order_id)}
                      >
                        <td>{txn.merchant_order_id}</td>
                        <td>{txn.merchant_id ?? '—'}</td>
                        <td>{txn.transaction_type ?? '—'}</td>
                        <td>
                          <span className={`badge ${STATUS_BADGES[txn.status] || 'text-bg-secondary'}`}>
                            {txn.status || 'unknown'}
                          </span>
                        </td>
                        <td className="text-nowrap">
                          {txn.exhausted_at || txn.last_attempted_at || txn.sent_at || txn.created_at || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {!result && !searchResults && (
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h2 className="h6 mb-0">Recent transactions</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={loadRecent}
              disabled={recentLoading}
            >
              {recentLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {recentError && <div className="alert alert-danger py-2">{recentError}</div>}

          {!recentError && (
            <div className="row g-3">
              <RecentOutcomeCard
                title="Last 10 successful"
                hint="Most recently delivered to the merchant successfully."
                items={recent.success}
                loading={recentLoading}
                onRowClick={handleRecentRowClick}
              />
              <RecentOutcomeCard
                title="Last 10 failed"
                hint="Failed but still has retries left - will be attempted again."
                items={recent.failed}
                loading={recentLoading}
                onRowClick={handleRecentRowClick}
              />
              <RecentOutcomeCard
                title="Last 10 exhausted"
                hint="Ran out of retries - needs a manual retry from the Retry-exhausted page."
                items={recent.exhausted}
                loading={recentLoading}
                onRowClick={handleRecentRowClick}
              />
            </div>
          )}
        </div>
      )}

      {result && (
        <button type="button" className="btn btn-sm btn-link px-0 mb-3" onClick={() => setResult(null)}>
          &larr; Back to {searchResults ? 'search results' : 'recent transactions'}
        </button>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {result && resendNotice && result.merchant_order_id === resendNotice.merchantOrderId && (
        <div className="alert alert-success py-2 d-flex align-items-center justify-content-between gap-2">
          <span>
            {resendNotice.message || 'Callback resend queued.'}
            {resendNotice.requestedAt ? ` (queued ${resendNotice.requestedAt})` : ''}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-success text-nowrap"
            onClick={() => runSearch(resendNotice.merchantOrderId)}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      )}

      {result && result._pending && (
        <div className="alert alert-info py-2">
          {result.message || 'Still searching CloudWatch Logs - try again in a few seconds.'}
        </div>
      )}

      {result && !result._pending && !result.found && (
        <div className="alert alert-secondary py-2">
          {result.message || 'No record found for this merchant_order_id.'}
        </div>
      )}

      {result && result.found && result.source === 'cloudwatch' && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h2 className="h6 mb-1">{result.merchant_order_id}</h2>
            <p className="text-muted small mb-3">
              No live Redis record (likely a callback that already succeeded and aged out) -
              showing matching log lines from the poller and worker instead, oldest first.
            </p>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '12rem' }}>Timestamp</th>
                    <th style={{ width: '10rem' }}>Log group</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {result.log_events.map((event, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={idx}>
                      <td className="text-nowrap">{event.timestamp || '—'}</td>
                      <td className="text-nowrap">
                        {event.logGroup && event.logGroup.includes('poller') ? 'poller' : 'worker'}
                      </td>
                      <td style={{ wordBreak: 'break-word' }}>{event.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ResendControl
              key={`cw-${result.merchant_order_id}`}
              merchantOrderId={result.merchant_order_id}
              callbackUrl={null}
              agedOut
              onQueued={(res) => handleResendQueued(result.merchant_order_id, res)}
            />
          </div>
        </div>
      )}

      {result && result.found && result.source !== 'cloudwatch' && (
        <>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h6 mb-0">{result.merchant_order_id}</h2>
                <span className={`badge ${STATUS_BADGES[result.status] || 'text-bg-secondary'}`}>
                  {STATUS_LABELS[result.status] || result.status || 'unknown'}
                </span>
              </div>

              <div className="row g-3 small">
                <div className="col-6 col-md-3">
                  <div className="text-muted">Merchant ID</div>
                  <div>{result.merchant_id ?? '—'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Amount</div>
                  <div>{result.amount ?? '—'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Transaction type</div>
                  <div>{result.transaction_type ?? '—'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Attempts</div>
                  <div>
                    {result.attempts ?? '—'}/{result.max_attempts ?? '—'}
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Instruction ID</div>
                  <div className="text-truncate" title={result.instruction_id || ''}>
                    {result.instruction_id ?? '—'}
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">QRPh invoice</div>
                  <div className="text-truncate" title={result.qrph_invoice || ''}>
                    {result.qrph_invoice ?? '—'}
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Rejection code</div>
                  <div>{result.rejection_code ?? '—'}</div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="text-muted">Callback URL</div>
                  <div className="text-truncate" title={result.callback_url || ''}>
                    {result.callback_url ?? '—'}
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-muted">Source</div>
                  <div>
                    {result.source === 'done_index'
                      ? 'Retained record (exhausted)'
                      : result.source === 'router_txn'
                        ? 'Router record only (no callback yet)'
                        : result.source === 'history'
                          ? 'Stored history (live record expired)'
                          : 'Live record'}
                  </div>
                </div>
              </div>

              {/* Merchant status/response (2026-07-28) - whichever of these is
                  populated depends entirely on how the last attempt went:
                  SendNotification only ever writes ONE of the two per
                  attempt (response on success, last_error on failure/
                  timeout/exhaustion), never both, so showing them
                  unconditionally (rather than gated to a specific `status`
                  value) is what actually reflects "the merchant's status or
                  response" regardless of outcome. */}
              {result.response !== null && result.response !== undefined && (
                <div className="alert alert-success py-2 mt-3 mb-0 small">
                  <strong>Merchant response:</strong>{' '}
                  {typeof result.response === 'string' ? result.response : JSON.stringify(result.response)}
                </div>
              )}

              {result.last_error && (
                <div className="alert alert-warning py-2 mt-3 mb-0 small">
                  <strong>Merchant status / error:</strong> {result.last_error}
                </div>
              )}

              {result.resend_in_progress && result.status !== 'success' && (
                <div className="alert alert-info py-2 mt-3 mb-0 small">
                  A manual resend is in progress for this transaction - status shows
                  {' '}<strong>{result.status}</strong> until the merchant accepts it again.
                </div>
              )}

              {result.status === 'awaiting_payment' && (
                <div className="border rounded p-3 mt-3 small">
                  <div className="fw-semibold mb-1">No payment validation (Leg 3) reached the router</div>
                  <p className="text-muted mb-2">
                    Either the customer has not paid, or Bancnet&rsquo;s Leg 3 request never reached the router (for
                    example, network flapping). In that case Bancnet tags the payment DS24.
                  </p>
                  {result.unpaid?.can_reject ? (
                    <>
                      <p className="text-muted mb-2">
                        Check Bancnet&rsquo;s records first, then send Java the rejection.
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          setUnpaidModalRecord(
                            awaitingModalRecord({
                              ...result.router_txn,
                              merchant_order_id: result.merchant_order_id,
                            })
                          )
                        }
                      >
                        Send rejected (DS24) to Java
                      </button>
                    </>
                  ) : (
                    result.unpaid?.reason && <p className="text-muted mb-0">{result.unpaid.reason}</p>
                  )}
                </div>
              )}

              {result.status === 'awaiting_leg3' && (
                <div className="alert alert-warning py-2 mt-3 mb-0 small">
                  The withdrawal was sent but Bancnet has not returned a result. After 5 minutes the router sends Java a
                  “no response yet” notice, and it then appears on the Java Pending card.
                </div>
              )}

              {result.status === 'awaiting_bancnet' && (
                <div className="border rounded p-3 mt-3 small">
                  <div className="fw-semibold mb-1">Stuck awaiting Bancnet&rsquo;s final result (Leg 5)</div>
                  <p className="text-muted mb-2">
                    {result.leg3?.result === 'RJCT'
                      ? `The router rejected this payment at Leg 3 (${result.leg3.rejection_code || 'RJCT'}), but Bancnet's final result never arrived.`
                      : 'Bancnet validated this payment at Leg 3 (ACTC), but its final result never arrived.'}{' '}
                    Java has not been told. Once you have confirmed the outcome in Bancnet&rsquo;s records, send it to Java.
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                      setStatusModalRecord(
                        awaitingModalRecord({
                          ...result.router_txn,
                          merchant_order_id: result.merchant_order_id,
                          instruction_id: result.instruction_id,
                        })
                      )
                    }
                  >
                    Send status to Java
                  </button>
                </div>
              )}

              <ManualUpdateNote marker={result.manual_update} waiting={result.source === 'router_txn'} />

              {result.status === 'success' && (
                <ResendControl
                  key={`live-${result.merchant_order_id}`}
                  merchantOrderId={result.merchant_order_id}
                  callbackUrl={result.callback_url}
                  agedOut={result.source === 'history'}
                  onQueued={(res) => handleResendQueued(result.merchant_order_id, res)}
                />
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 mb-3">Audit flow</h2>
              <AuditFlow result={result} />
            </div>
          </div>

          {result.source !== 'router_txn' && (
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Stage</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {TIMELINE_FIELDS.map((field) => {
                    const { text, muted } = describeStage(field, result);
                    return (
                      <tr key={field.id}>
                        <td>
                          <div>{field.label}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {field.hint}
                          </div>
                        </td>
                        <td className={muted ? 'text-muted' : ''}>{text}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Per-attempt reference (2026-07-28) - every row above only ever
              shows the LATEST attempt's fields (that's the whole record's
              own design), so a transaction that failed once and succeeded
              on a second try had no visible trace of the first attempt
              beyond a leftover last_error (see CLAUDE.md, "Success writes
              left a stale last_error"). This table is the actual fix for
              that: one row per attempt SendNotification has ever recorded
              for this merchant_order_id, oldest first. Absent/empty on
              records written before attempt_history existed - nothing to
              backfill retroactively. */}
          {result.attempt_history && result.attempt_history.length > 0 && (
            <div className="card border-0 shadow-sm mt-3">
              <div className="card-body">
                <h2 className="h6 mb-1">Attempt history</h2>
                <p className="text-muted small mb-3">
                  {result.attempt_history.length} attempt{result.attempt_history.length === 1 ? '' : 's'} recorded
                  {result.attempts ? ` (attempts: ${result.attempts}/${result.max_attempts ?? '—'})` : ''}.
                </p>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Attempt</th>
                        <th>Outcome</th>
                        <th>Timestamp</th>
                        <th>Duration</th>
                        <th>Response / error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.attempt_history.map((entry, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${entry.attempt}-${idx}`}>
                          <td>{entry.attempt ?? idx + 1}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGES[entry.outcome] || 'text-bg-secondary'}`}>
                              {entry.outcome || 'unknown'}
                            </span>
                          </td>
                          <td className="text-nowrap">{entry.finished_at || '—'}</td>
                          <td className="text-nowrap">
                            {typeof entry.duration_ms === 'number' ? `${entry.duration_ms.toLocaleString()}ms` : '—'}
                          </td>
                          <td style={{ wordBreak: 'break-word' }}>
                            {entry.error
                              || (entry.response !== null && entry.response !== undefined
                                ? (typeof entry.response === 'string' ? entry.response : JSON.stringify(entry.response))
                                : '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Audit trail for manual resends (2026-10-08) - who sent the
              merchant this notification again, and when. Written by
              P2MJava::resendCallback(). */}
          {result.manual_resends && result.manual_resends.length > 0 && (
            <div className="card border-0 shadow-sm mt-3">
              <div className="card-body">
                <h2 className="h6 mb-1">Manual resends</h2>
                <p className="text-muted small mb-3">
                  Resent by an operator after the original callback had already succeeded.
                </p>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Requested at</th>
                        <th>Requested by</th>
                        <th>Rebuilt from</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.manual_resends.map((entry, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${entry.requested_at}-${idx}`}>
                          <td className="text-nowrap">{entry.requested_at || '—'}</td>
                          <td>{entry.requested_by || '—'}</td>
                          <td>{entry.source === 'history' ? 'Stored history (record had expired)' : 'Live record'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <PendingStatusModal
        show={Boolean(statusModalRecord)}
        source="java"
        label="Awaiting Bancnet response"
        submitJava={updateAwaitingDepositStatus}
        paidOnly={(statusModalRecord?.leg3_deposit_type || statusModalRecord?.deposit_type) === 'static'}
        record={statusModalRecord}
        onClose={() => setStatusModalRecord(null)}
        // Refresh so the "status was sent" note and audit flow update. The
        // modal stays open showing its own success message until closed.
        onSuccess={(record) => runSearch(record.merchant_order_id)}
      />

      <PendingStatusModal
        show={Boolean(unpaidModalRecord)}
        source="java"
        label="Deposit with no Leg 3"
        submitJava={rejectUnpaidDeposit}
        rejectOnly
        record={unpaidModalRecord}
        onClose={() => setUnpaidModalRecord(null)}
        onSuccess={(record) => runSearch(record.merchant_order_id)}
      />
    </div>
  );
}
